import * as React from 'react';
import * as styles from './RecurringEvents.css';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import { LocalizationService } from '../../../services/LocalizationService';
import EventService from '../../../services/EventService';
import { IMessage } from '../../../interfaces/IMessage';
import { IASkeleton } from '../../skeleton/Skeleton';
import DateTimeService from '../../../services/DateTimeService';
import { RecurrenceHelper } from '../../../RecurrenceHelper';
import { IATooltip } from '../../tooltip/Tooltip';
import { IAIcon } from '../../icon/Icon';

interface IProps {
  environment: IEnvironment;
  instanceName: string;
  tenant: string;
  onOpenEvent: (event: IMessage) => void;
}

interface IState {
  isFetchingEvents: boolean;
  errorFetchingEvents: boolean;
  events: IMessage[];
}

export class IARecurringEvents extends React.Component<IProps, IState> {
  private readonly localizationService = new LocalizationService();
  private readonly dateTimeService = new DateTimeService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      isFetchingEvents: false,
      errorFetchingEvents: false,
      events: [],
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }
  
  componentDidMount(): void {
    this.setState({ isFetchingEvents: true });
    // Fetch events from the server
    this.fetchEvents();
  }

  private async fetchEvents() {
    EventService.getRecurringEvents(this.props.environment, this.props.tenant, this.props.instanceName).then((result) => {
      console.log("Fetched events:", result);
      this.setState({ events: result.messages, errorFetchingEvents: false });
    }).catch((error) => {
      console.error("Error fetching events:", error);
      this.setState({ errorFetchingEvents: true });
    }).finally(() => {
      this.setState({ isFetchingEvents: false });
    });
  }
  
  private getSkeletonListElements(): JSX.Element[] {
    let skeletonListElements = [];
    for (let i = 0; i < 5; i++) {
      skeletonListElements.push(
        <IASkeleton
          skeleton={{
            height: 35,
            width: 200,
            borderRadius: 5,
          }}
        />
      );
    }
    return skeletonListElements;
  }

  private getListElements(): JSX.Element[] {
    return this.state.events.map((event) => {
      const data = event.recurrenceData;

      const intervalString = {
        "daily": this.localizationService.strings.Events_RecurrenceDays,
        "weekly": this.localizationService.strings.Events_RecurrenceWeeks,
        "monthly": this.localizationService.strings.Events_RecurrenceMonths,
        "yearly": this.localizationService.strings.Events_RecurrenceYears
      };

      let statusColor = "green";
      let statusText = this.localizationService.strings.ContentCreator_Active;
      if (event.recurrenceEndDate && new Date(event.recurrenceEndDate) < new Date()) {
        statusColor = "red";
        statusText = this.localizationService.strings.ContentCreator_Expired;
      }

      return (
        <div
          className={styles.IA_recurringElement}
          onClick={() => this.props.onOpenEvent(event)}
        >
          {event.image && <img src={event.image} alt={event.title} />}
          <div className={styles.IA_contentWrapper}>
            <span>{event.title}</span>
            <div className={styles.IA_contentInfo}>
              <span style={{ color: statusColor }}>{statusText}</span>
              <IATooltip
                content="Tooltip"
                type="bubble"
                componentContent={<div className={styles.IA_toolTipWrapper}>
                  <div className={styles.IA_tooltipText}>{this.localizationService.strings.ContentCreator_RecurrenceStatus}: <span style={{ color: statusColor }}>{statusText}</span></div>
                  <div className={styles.IA_tooltipText}>{this.localizationService.strings.ContentCreator_FirstOccurrence}: <span>{this.dateTimeService.ConvertToDDMMYYYY(event.startDate)}</span></div>
                  <div className={styles.IA_tooltipText}>{this.localizationService.strings.ContentCreator_LastOccurrence}: <span>{event.recurrenceEndDate ? this.dateTimeService.ConvertToDDMMYYYY(event.recurrenceEndDate) : this.localizationService.strings.ContentCreator_NoEndDate}</span></div>
                  <div className={styles.IA_tooltipText}>{`${this.localizationService.strings.ContentCreator_RepeatEvery} ${data.interval} ${intervalString[data.recurrenceType]}`}</div>
                  {data.recurrenceType === "daily" &&
                    <div>{this.localizationService.strings.ContentCreator_EveryDay}</div>
                  }
                  {data.recurrenceType === "weekly" &&
                    <div className={styles.IA_weeklyWrapper}>{data.daysOfWeek?.map((day) => { 
                        return <span className={""}>{RecurrenceHelper.getLocalizedDay(day, this.localizationService)}</span>
                      })}
                    </div>
                  }
                  {data.recurrenceType === "monthly" &&
                    <div>{RecurrenceHelper.getMonthlyRecurrencePatternTypes(event.startDate, this.localizationService).find(x => x.monthlyPattern === data.monthlyPattern).text}</div>
                  }
                  {data.recurrenceType === "yearly" &&
                    <div>{this.localizationService.strings.ContentCreator_EveryYear + " " + this.dateTimeService.ConvertToDDMM(event.startDate)}</div>
                  }
                </div>}
                borderRadius={5}
              >
                <IAIcon
                  color='#333333'
                  size={16}
                  url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Info/SVG/ic_fluent_info_28_regular.svg"
                />
              </IATooltip>
            </div>
          </div>
        </div>
      );
    });
  }

  render(): React.ReactNode {
    return (<div className={styles.IA_wrapper}>
      {this.state.isFetchingEvents && this.getSkeletonListElements()}
      {!this.state.isFetchingEvents && this.getListElements()}
    </div>)
  }
}